import { useEffect, useState } from "react";
import { ShareModes } from "../enums/shareFlow";
import { hasClassMemberSubmitted } from "../helpers/ClassMemberActivities";
import { getTestResultForClassMember } from "../networking/tests";

export enum ExistingTestResultStates {
    PENDING = "PENDING",
    NONE = "NONE",
    SUBMITTED = "SUBMITTED",
}

interface UseExistingTestResultParams {
    testId: string | undefined;
    shareMode: ShareModes | null | undefined;
    classMemberId: string | null;
    isClassMemberSessionReady: boolean;
    isPreview: boolean;
}

/**
 * Only login-gated TESTs can be gated: without a member identity (public class, legacy activity)
 * there is nothing to key a previous submission on, and practices are repeatable by design. Those
 * all resolve to NONE.
 *
 * Fails open — a lookup error resolves to NONE rather than PENDING or SUBMITTED. Locking a student
 * out of a test they never took because the results endpoint hiccuped is worse than letting a
 * duplicate through; real dedup would have to live in the backend, which has none yet.
 */
const useExistingTestResult = (params: UseExistingTestResultParams): ExistingTestResultStates => {
    const [existingResultState, setExistingResultState] = useState<ExistingTestResultStates>(
        ExistingTestResultStates.PENDING
    );

    const testId = params.testId;
    const classMemberId = params.classMemberId;
    const isGatedActivity = params.shareMode === ShareModes.TEST && !params.isPreview;

    useEffect(() => {
        // Resolved before the session wait: these verdicts don't depend on who is logged in, and the
        // author's /create/preview has no member session that would ever settle.
        if (!testId || !isGatedActivity) {
            setExistingResultState(ExistingTestResultStates.NONE);
            return;
        }
        // A missing member is only meaningful once the session has settled — until then it just means
        // "still restoring", and treating it as "no member, nothing to check" would open the gate on
        // exactly the member we need to look up.
        if (!params.isClassMemberSessionReady) {
            setExistingResultState(ExistingTestResultStates.PENDING);
            return;
        }
        if (!classMemberId) {
            setExistingResultState(ExistingTestResultStates.NONE);
            return;
        }

        let cancelled = false;
        setExistingResultState(ExistingTestResultStates.PENDING);

        (async () => {
            try {
                const result = await getTestResultForClassMember(testId, classMemberId);
                if (cancelled) return;
                const submitted = hasClassMemberSubmitted({ shareMode: ShareModes.TEST }, result);
                setExistingResultState(submitted ? ExistingTestResultStates.SUBMITTED : ExistingTestResultStates.NONE);
            } catch (e) {
                if (cancelled) return;
                console.warn("Failed to check for an existing test result", e);
                setExistingResultState(ExistingTestResultStates.NONE);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [testId, classMemberId, isGatedActivity, params.isClassMemberSessionReady]);

    return existingResultState;
};

export default useExistingTestResult;
